// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Focus mode, as React state (M8-72).
//
// THE DEFECT THIS REPLACES
// ------------------------
// `AppShell` and `MachineStage` each held a `useState(isPlayFocused)` and subscribed to
// `watchPlayFocus` from an effect. React runs a child's effects before its parent's, so on any load
// that mounts both in one commit — the front door, a reload, a home-screen launch — the stage's
// mount effect had already set the signal, for a player whose stored choice is `on`, by the time
// the shell subscribed. The shell's state was read during render, before the change; the listener
// that would have told it arrived after the change. So the stage said "Leave focus mode" over a
// shell still drawing both bars, and every plunger pull afterwards found `isPlayFocused()` already
// true and did nothing — which is the half the phone reported. Reproduced in Chrome at 390 x 844
// on 2 Sep 2026: `.shell` without `shell--focused`, a `.topbar-row`, a `nav`, and a menu row
// reading "Leave focus mode". Arriving from the arcade list never showed it, because the shell was
// already subscribed by then, which is why no desktop session had seen it.
//
// `useSyncExternalStore` exists for exactly this: it re-reads the snapshot after it subscribes and
// re-renders if the two disagree, so a change that lands between a component's render and its
// subscription is not lost. It is the shape `useViewportWidth` and `ThemeToggle` already use, one
// directory away; the only reason it was not used here is that this hook did not exist.
//
// Not in `playFocus.ts`, whose header promises it imports nothing at all.

import { useSyncExternalStore } from 'react'
import { isPlayFocused, watchPlayFocus } from './playFocus'

/** Nothing is ever focused on a server, where there is no shell to collapse. */
function neverFocused(): boolean {
  return false
}

/** Is the shell out of the way? Kept current, and correct from the first render that can be. */
export function usePlayFocus(): boolean {
  return useSyncExternalStore(watchPlayFocus, isPlayFocused, neverFocused)
}
